import {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useState,
} from "react";
import { useLocation, useParams } from "react-router-dom";
import api from "api/axios";
import toast from "react-hot-toast";
import { handleAxiosAndCaughtError } from "utils";
import { EvaluationHistoryRecord, FileSystemItem } from "types";
import Loader from "components/Loader";
import { useTranslation } from "react-i18next";

interface AgentEvaluateContextType {
  datasets: FileSystemItem[];
  loading: boolean;
  error: string | null;
  fetchDatasets: () => Promise<void>;
  uploadDatasets: (files: File[]) => Promise<void>;
  deleteDataset: (datasetId: string) => Promise<void>;
  setDatasets: (datasets: FileSystemItem[]) => void;
  evaluateAgent: (evaluation_dataset_id: string) => Promise<void>;
  fetchEvaluationHistoryRecords: (signal?: AbortSignal) => Promise<void>;
  evaluationHistoryRecords: EvaluationHistoryRecord[];
  setEvaluationHistoryRecords: (results: EvaluationHistoryRecord[]) => void;
}

const AgentEvaluateContext = createContext<
  AgentEvaluateContextType | undefined
>(undefined);

interface AgentEvaluateProviderProps {
  children: ReactNode;
}

export const AgentEvaluationProvider = ({
  children,
}: AgentEvaluateProviderProps) => {
  const { t } = useTranslation();
  const { id: agentId } = useParams();
  const location = useLocation();
  const [datasets, setDatasets] = useState<FileSystemItem[]>([]);
  const [evaluationHistoryRecords, setEvaluationHistoryRecords] = useState<
    EvaluationHistoryRecord[]
  >([]);
  const [loading, setLoading] = useState(true);
  const [loadingTxt, setLoadingTxt] = useState(t("library.loadingDatasets"));
  const [error, setError] = useState<string | null>(null);

  const fetchEvaluationHistoryRecords = useCallback(
    async (signal?: AbortSignal): Promise<void> => {
      if (!agentId) return;
      setLoading(true);
      setError(null);
      try {
        const res = await api.get(`/agents/${agentId}/evaluations/results`, {
          params: { skip: 0, take: 999 },
          ...(signal instanceof AbortSignal ? { signal } : {}),
        });
        setEvaluationHistoryRecords(
          res.data.items as EvaluationHistoryRecord[]
        );
      } catch (err: any) {
        if (signal?.aborted || err.name === "CanceledError") {
          return;
        }
        setError("Failed to fetch evaluation results");
        handleAxiosAndCaughtError(err);
      } finally {
        setLoading(false);
      }
    },
    [agentId]
  );

  const fetchDatasets = useCallback(
    async (signal?: AbortSignal) => {
      if (!agentId) return;
      setLoading(true);
      setError(null);

      const maxRetries = 3;
      const retryDelay = 1000;

      for (let attempt = 1; attempt <= maxRetries; attempt++) {
        try {
          const res = await api.get(`/agents/${agentId}/evaluations/datasets`, {
            params: {
              skip: 0,
              take: 999,
            },
            ...(signal instanceof AbortSignal ? { signal } : {}),
          });
          setDatasets(res.data.items);
          return; // Success
        } catch (err: any) {
          if (signal?.aborted || err.name === "CanceledError") {
            return; // Aborted
          }
          if (err.response?.status === 500 && attempt < maxRetries) {
            await new Promise((resolve) => setTimeout(resolve, retryDelay));
            continue;
          } else {
            setError("Failed to fetch datasets");
            handleAxiosAndCaughtError(err);
            return;
          }
        } finally {
          setLoading(false);
        }
      }
    },
    [agentId]
  );

  const uploadDatasets = useCallback(
    async (files: File[]) => {
      if (!agentId) return;
      setLoading(true);
      setError(null);
      const formData = new FormData();
      files.forEach((file) => {
        formData.append("datasets", file);
      });
      try {
        await api.post(
          `/agents/${agentId}/evaluations/datasets/upload`,
          formData,
          {
            headers: { "Content-Type": "multipart/form-data" },
          }
        );
        toast.success("Datasets uploaded successfully");
        await fetchDatasets();
      } catch (err) {
        setError("Failed to upload datasets");
        handleAxiosAndCaughtError(err);
      } finally {
        setLoading(false);
      }
    },
    [agentId, fetchDatasets]
  );

  const deleteDataset = useCallback(
    async (datasetId: string) => {
      if (!agentId) return;
      setLoading(true);
      setError(null);
      try {
        await api.delete(
          `/agents/${agentId}/evaluations/datasets/${datasetId}`
        );
        toast.success("Dataset deleted");
        setDatasets((prev) => prev.filter((d) => d.id !== datasetId));
      } catch (err) {
        setError("Failed to delete dataset");
        handleAxiosAndCaughtError(err);
      } finally {
        setLoading(false);
      }
    },
    [agentId]
  );

  const evaluateAgent = useCallback(
    async (evaluation_dataset_id: string) => {
      if (!agentId) return;
      setLoading(true);
      setError(null);
      try {
        await api.post(`/agents/${agentId}/evaluations/evaluate`, {
          evaluation_dataset_id,
        });
        toast.success("Evaluation started successfully");
        await fetchEvaluationHistoryRecords();
      } catch (err) {
        setError("Failed to start evaluation");
        handleAxiosAndCaughtError(err);
      } finally {
        setLoading(false);
      }
    },
    [agentId, fetchEvaluationHistoryRecords]
  );

  useEffect(() => {
    if (agentId) {
      const abortController = new AbortController();
      const signal = abortController.signal;
      const fetchAll = async () => {
        setLoading(true);
        setError(null);
        try {
          if (location.pathname.includes("datasets")) {
            setLoadingTxt(t("library.loadingDatasets"));
            await fetchDatasets(signal);
          } else if (location.pathname.includes("history")) {
            setLoadingTxt(t("library.loadingEvaluationHistory"));
            await fetchEvaluationHistoryRecords(signal);
          }
        } catch (err) {
          setError("Failed to fetch initial data");
        } finally {
          setLoading(false);
        }
      };
      fetchAll();
      return () => abortController.abort();
    }
  }, [
    agentId,
    fetchDatasets,
    fetchEvaluationHistoryRecords,
    location.pathname,
  ]);

  useEffect(() => {
    if (agentId && location.pathname.endsWith("/evaluate")) {
      const abortController = new AbortController();
      const signal = abortController.signal;
      const fetchInitial = async () => {
        setLoading(true);
        setError(null);
        setLoadingTxt(t("library.loadingDatasets"));
        try {
          await fetchDatasets(signal);
        } catch (err) {
          setError("Failed to fetch initial data");
        } finally {
          setLoading(false);
        }
      };
      fetchInitial();
      return () => abortController.abort();
    }
  }, [agentId, fetchDatasets, location.pathname]);

  const value: AgentEvaluateContextType = {
    datasets,
    loading,
    error,
    fetchDatasets,
    uploadDatasets,
    deleteDataset,
    setDatasets,
    evaluateAgent,
    fetchEvaluationHistoryRecords,
    evaluationHistoryRecords,
    setEvaluationHistoryRecords,
  };

  return (
    <AgentEvaluateContext.Provider value={value}>
      {loading ? (
        <div className="h-[30vh] w-full  ">
          <Loader
            txt={loadingTxt}
            className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2"
          />
        </div>
      ) : (
        children
      )}
    </AgentEvaluateContext.Provider>
  );
};

export const useAgentEvaluation = (): AgentEvaluateContextType => {
  const context = useContext(AgentEvaluateContext);
  if (context === undefined) {
    throw new Error(
      "useAgentEvaluate must be used within an AgentEvaluateProvider"
    );
  }
  return context;
};
